import {
  Facebook,
  Github,
  Instagram,
  Linkedin,
  Send,
  Twitter,
  Youtube,
  type LucideIcon,
} from 'lucide-react';

export type SocialLinkItem = {
  platform: string;
  url: string;
};

const SOCIAL_ICONS: Record<string, LucideIcon> = {
  facebook: Facebook,
  twitter: Twitter,
  x: Twitter,
  linkedin: Linkedin,
  instagram: Instagram,
  telegram: Send,
  youtube: Youtube,
  github: Github,
};

const SOCIAL_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  twitter: 'X / Twitter',
  x: 'X / Twitter',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  telegram: 'Telegram',
  youtube: 'YouTube',
  github: 'GitHub',
};

export function normalizeSocialPlatform(platform: string): string {
  const key = platform.trim().toLowerCase();
  return key === 'x' ? 'twitter' : key;
}

export function normalizeSocialLinks(
  input?: Record<string, string> | SocialLinkItem[] | null
): SocialLinkItem[] {
  if (!input) return [];

  const entries: SocialLinkItem[] = Array.isArray(input)
    ? input.map((item) => ({
        platform: normalizeSocialPlatform(String(item.platform ?? '')),
        url: String(item.url ?? '').trim(),
      }))
    : Object.entries(input).map(([platform, url]) => ({
        platform: normalizeSocialPlatform(platform),
        url: String(url ?? '').trim(),
      }));

  const seen = new Set<string>();
  const links: SocialLinkItem[] = [];

  for (const entry of entries) {
    if (!entry.platform || !entry.url || seen.has(entry.platform)) continue;
    seen.add(entry.platform);
    links.push(entry);
  }

  return links;
}

export function getSocialIcon(platform: string): LucideIcon | null {
  return SOCIAL_ICONS[normalizeSocialPlatform(platform)] ?? null;
}

export function getSocialLabel(platform: string): string {
  const key = normalizeSocialPlatform(platform);
  return SOCIAL_LABELS[key] ?? platform;
}

type SocialLinksListProps = {
  links: SocialLinkItem[];
  className?: string;
  iconClassName?: string;
};

export function SocialLinksList({
  links,
  className = 'flex flex-wrap justify-center gap-3',
  iconClassName = 'w-5 h-5',
}: SocialLinksListProps) {
  if (!links.length) return null;

  return (
    <div className={className}>
      {links.map(({ platform, url }) => {
        const Icon = getSocialIcon(platform);
        const label = getSocialLabel(platform);

        return (
          <a
            key={`${platform}-${url}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-nature-muted hover:text-nature-text transition-colors"
            aria-label={label}
            title={label}
          >
            {Icon ? <Icon className={iconClassName} /> : <span className="text-sm font-medium">{label}</span>}
          </a>
        );
      })}
    </div>
  );
}
